<template>
  <div class="page-container">
    <div class="page-title-container">
      <h1 class="page-title">{{ editing ? 'Editar tipo de planilla' : 'Nuevo tipo de planilla' }}</h1>
    </div>
    <div>
      <form class="custom-form" @submit.prevent="save" novalidate>
        <b-card tag="article" no-body style="max-width: 760px" class="custom-card">
          <b-card-header header-tag="section" class="custom-card-header d-flex align-items-center">
            <h3>{{ editing ? 'Datos del tipo de planilla' : 'Datos del nuevo tipo de planilla' }}</h3>
          </b-card-header>
          <b-card-body class="custom-card-body p-0">
            <section class="form-section mb-0">
              <div class="row">
                <div class="col-md-6">
                  <div class="form-group">
                    <label>Codigo *</label>
                    <input type="text" v-model="form.code" autocomplete="off" placeholder="Ej: MENS"
                      class="custom-form-control" :class="{ 'custom-form-control-error': $v.form.code.$error }" />
                    <FormError v-if="$v.form.code.$error && !$v.form.code.required" message="Codigo es requerido" />
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group">
                    <label>Nombre *</label>
                    <input type="text" v-model="form.name" autocomplete="off" placeholder="Ej: Planilla mensual"
                      class="custom-form-control" :class="{ 'custom-form-control-error': $v.form.name.$error }" />
                    <FormError v-if="$v.form.name.$error && !$v.form.name.required" message="Nombre es requerido" />
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group">
                    <label>Frecuencia *</label>
                    <FormSelect
                      id="frequency"
                      v-model="form.frequency"
                      :items="frequencyList"
                      defaultOption="Elegir frecuencia"
                      :showError="$v.form.frequency.$error && !$v.form.frequency.required"
                    />
                    <FormError v-if="$v.form.frequency.$error && !$v.form.frequency.required" message="Frecuencia es requerida" />
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group">
                    <label>Categoria tributaria *</label>
                    <FormSelect
                      id="taxCategory"
                      v-model="form.taxCategory"
                      :items="taxCategoryList"
                      defaultOption="Elegir categoria"
                      :showError="$v.form.taxCategory.$error && !$v.form.taxCategory.required"
                    />
                    <FormError v-if="$v.form.taxCategory.$error && !$v.form.taxCategory.required" message="Categoria es requerida" />
                  </div>
                </div>

                <!-- Toggles -->
                <div class="col-md-12">
                  <hr />
                  <h6 class="text-muted mb-3">Configuracion de conceptos</h6>
                </div>
                <div class="col-md-6">
                  <div class="form-group d-flex align-items-center">
                    <b-form-checkbox v-model="form.affectsSocialBenefits" switch size="lg" class="mr-2" />
                    <label class="mb-0">Afecta beneficios sociales</label>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group d-flex align-items-center">
                    <b-form-checkbox v-model="form.includesFamilyAllowance" switch size="lg" class="mr-2" />
                    <label class="mb-0">Incluye asignacion familiar</label>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group d-flex align-items-center">
                    <b-form-checkbox v-model="form.includesPension" switch size="lg" class="mr-2" />
                    <label class="mb-0">Incluye pension</label>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group d-flex align-items-center">
                    <b-form-checkbox v-model="form.includesEssalud" switch size="lg" class="mr-2" />
                    <label class="mb-0">Incluye EsSalud</label>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group d-flex align-items-center">
                    <b-form-checkbox v-model="form.includesIr" switch size="lg" class="mr-2" />
                    <label class="mb-0">Incluye impuesto a la renta</label>
                  </div>
                </div>

                <div class="col-md-12"><hr /></div>

                <div class="col-md-6">
                  <div class="form-group">
                    <label>Regla de pago</label>
                    <input type="text" v-model="form.payDayRule" autocomplete="off"
                      placeholder="Ej: Ultimo dia habil del mes" class="custom-form-control" />
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group mb-0">
                    <label>Descripcion</label>
                    <textarea v-model="form.description" rows="2" class="custom-form-control"
                      placeholder="Descripcion opcional"></textarea>
                  </div>
                </div>
              </div>
            </section>
            <div class="form-group-buttons text-right">
              <router-link :to="{ name: 'payroll-type-list' }" class="button button-light">{{ editing ? 'Volver' : 'Cancelar' }}</router-link>
              <button class="button button-primary" type="submit">Guardar</button>
            </div>
          </b-card-body>
        </b-card>
      </form>
    </div>
  </div>
</template>

<script>
import { Alert, FormError, FormSelect } from 'wize-admin'
import { PayrollTypeService } from '@/core/services'
import { Constants } from '@/core/utils'
import { required } from 'vuelidate/lib/validators'

export default {
  components: { FormError, FormSelect },
  data() {
    return {
      form: {
        code: null,
        name: null,
        frequency: null,
        taxCategory: null,
        affectsSocialBenefits: true,
        includesFamilyAllowance: true,
        includesPension: true,
        includesEssalud: true,
        includesIr: true,
        payDayRule: null,
        description: null,
      },
      editing: false,
      frequencyList: Object.values(Constants.PayrollFrequency),
      taxCategoryList: Object.values(Constants.PayrollTaxCategory),
    }
  },
  validations: {
    form: {
      code: { required },
      name: { required },
      frequency: { required },
      taxCategory: { required },
    },
  },
  watch: { '$route.params.id'() { if (this.$route.params.id) this.load(this.$route.params.id) } },
  methods: {
    async save() {
      this.$v.$touch()
      if (this.$v.$error) return
      try {
        this.$store.dispatch('app/loading', true)
        let response
        if (!this.editing) response = await PayrollTypeService.save(this.form)
        else response = await PayrollTypeService.update(this.$route.params.id, this.form)
        Alert.success('Se almaceno la informacion correctamente')
        if (!this.editing) {
          this.$router.push({ name: 'payroll-type-edit', params: { id: response.payload.id } })
          this.editing = true
        }
      } catch (ex) {
        console.error(ex); Alert.error(ex.errors ? ex.errors.message : 'Error al guardar')
      } finally { this.$store.dispatch('app/loading', false) }
    },
    async load(id) {
      try {
        this.$store.dispatch('app/loading', true)
        const resp = await PayrollTypeService.get(id)
        this.form = { ...resp.payload }
        this.editing = true
      } catch (e) {
        console.error(e); Alert.error(e.errors ? e.errors.message : 'Error al cargar')
        this.$router.push({ name: 'payroll-type-list' })
      } finally { this.$store.dispatch('app/loading', false) }
    },
  },
  created() { if (this.$route.params.id) this.load(this.$route.params.id) },
}
</script>
